import React from 'react';
import type { ComponentPropsWithoutRef } from 'react';

import PropTypes from 'prop-types';

import styled, { DefaultTheme } from 'styled-components';

export type IconColor =
    | 'darkGray'
    | 'mediumGray'
    | 'lightGray'
    | 'superLightGray'
    | 'greyRgba28'
    | 'purple'
    | 'purpleRgba24'
    | 'purpleRgba40'
    | 'red'
    | 'white'
    | 'white_base';

export type IconSize = '16px' | '20px' | '24px' | '32px' | '48px' | '56px' | '64px' | '80px' | '96px' | '100%';
export type IconRightIndent = '4px' | '8px' | '12px' | '16px' | '24px' | '32px';
export type IconLeftIndent = '2px' | '4px' | '8px' | '12px' | '16px' | '24px' | '32px';

type IconTheme = DefaultTheme & {
    black: string;
    grey_72_lc: string;
    grey_40_lc: string;
    grey_8_lc: string;
    grey_28_tr: string;
    purple_100: string;
    purple_24_tr: string;
    purple_40_tr: string;
    pink_100: string;
    white: string;
    white_base: string;
};

const defaultSize: IconSize = '24px';

interface ContainerProps {
    size?: IconSize;
    rightIndent?: IconRightIndent;
    leftIndent?: IconLeftIndent;
    color?: IconColor;
}

const getTheme = (props: { theme: DefaultTheme }): IconTheme => props.theme as IconTheme;
const shouldForwardIconProp = (prop: string) => !['size', 'rightIndent', 'leftIndent', 'color'].includes(prop);

const getIconColor = (props: ContainerProps & { theme: DefaultTheme }) => {
    const theme = getTheme(props);
    switch (props.color) {
        case 'darkGray':
            return theme.black;
        case 'mediumGray':
            return theme.grey_72_lc;
        case 'lightGray':
            return theme.grey_40_lc;
        case 'superLightGray':
            return theme.grey_8_lc;
        case 'greyRgba28':
            return theme.grey_28_tr;
        case 'purple':
            return theme.purple_100;
        case 'purpleRgba24':
            return theme.purple_24_tr;
        case 'purpleRgba40':
            return theme.purple_40_tr;
        case 'red':
            return theme.pink_100;
        case 'white':
            return theme.white;
        case 'white_base':
            return theme.white_base;
        default:
            return theme.black;
    }
};

const Container = styled.span.withConfig({ shouldForwardProp: shouldForwardIconProp })<ContainerProps>`
    display: inline-block;
    height: ${(props) => props.size ?? defaultSize};
    margin-right: ${(props) => props.rightIndent ?? null};
    margin-left: ${(props) => props.leftIndent ?? null};

    svg {
        width: ${(props) => props.size ?? defaultSize};
        height: ${(props) => props.size ?? defaultSize};
        --accent: ${(props) => getIconColor(props)};
    }
`;

type StyledSVGProps = {
    id: string;
    className?: string;
};

const renderSVG = ({ id, className }: StyledSVGProps) => {
    return (
        <svg className={className}>
            <use xlinkHref={`/i/svg-icons/icons.svg#${id}`} />
        </svg>
    );
};

const StyledSVGIcon = styled(renderSVG)`
    display: inline-block;
`;

export interface IconProps extends ContainerProps {
    name: string;
}

export const Icon = ({ rightIndent, leftIndent, color, size, name }: IconProps) => {
    return (
        <Container rightIndent={rightIndent} leftIndent={leftIndent} color={color} size={size}>
            <StyledSVGIcon id={name} />
        </Container>
    );
};

type IconPropTypes = ComponentPropsWithoutRef<typeof Icon>;

Icon.propTypes = {
    name: PropTypes.string.isRequired,
    color: PropTypes.oneOf([
        'darkGray',
        'mediumGray',
        'lightGray',
        'superLightGray',
        'greyRgba28',
        'purple',
        'purpleRgba24',
        'purpleRgba40',
        'red',
        'white',
        'white_base',
    ]),
    size: PropTypes.oneOf(['16px', '20px', '24px', '32px', '48px', '56px', '64px', '80px', '96px', '100%']),
    rightIndent: PropTypes.oneOf(['4px', '8px', '12px', '16px', '24px', '32px']),
    leftIndent: PropTypes.oneOf(['2px', '4px', '8px', '12px', '16px', '24px', '32px']),
} as PropTypes.ValidationMap<IconPropTypes>;
